import { useState } from 'react';
import { Sparkles, Award, Upload, Loader2, Plus } from 'lucide-react';
import { apiService } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { CosmeticList } from '@/components/config/CosmeticList';
import styles from '@/pages/GradesPage.module.scss';

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/** Création d'un nouvel ornement (nametag) ou rank tag : dépose la texture et câble le RP + la config. */
export function CosmeticCreate({ onCreated }: { onCreated?: () => void }) {
  const project = useGameStore((s) => s.activeGameId);
  const [type, setType] = useState<'nametag' | 'ranktag'>('nametag');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [listRefresh, setListRefresh] = useState(0);

  const slug = name.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
  const folder = type === 'ranktag' ? 'rank_tag' : 'cosmetic_tag';
  const fileBase = type === 'ranktag' ? `${slug}_rank_tag` : `${slug}_nametag`;

  const onFile = (f: File) => {
    setFile(f);
    const r = new FileReader();
    r.onload = () => setPreview(r.result as string);
    r.readAsDataURL(f);
  };

  const create = async () => {
    if (!slug || !file) { setFeedback({ ok: false, msg: 'Nom et image PNG requis.' }); return; }
    setCreating(true);
    setFeedback(null);
    setLog([]);
    const dataUrl = await readAsDataUrl(file);
    const res = await apiService.createCosmetic({ project, type, name: slug, fileName: file.name, fileData: dataUrl });
    setCreating(false);
    if (res.data) {
      setFeedback({ ok: res.data.success, msg: res.data.message });
      setLog([...(res.data.editedFiles ?? []).map((f) => `RP : ${f}`), ...(res.data.containers ?? [])]);
      if (res.data.success) { setName(''); setFile(null); setPreview(null); setListRefresh((k) => k + 1); onCreated?.(); }
    } else {
      setFeedback({ ok: false, msg: res.error ?? 'Échec' });
    }
  };

  return (
    <div className={styles.detailInner}>
      <div className={styles.detailHeader}>
        <div className={styles.detailIdentity}>
          <Sparkles size={22} className={styles.detailIcon} />
          <span className={styles.detailName}>Créer un cosmetic</span>
        </div>
      </div>

      <div className={styles.tabBar}>
        <button type="button" className={`${styles.tab} ${type === 'nametag' ? styles.tabActive : ''}`} onClick={() => setType('nametag')}>
          <Sparkles size={14} /> Ornement (nametag)
        </button>
        <button type="button" className={`${styles.tab} ${type === 'ranktag' ? styles.tabActive : ''}`} onClick={() => setType('ranktag')}>
          <Award size={14} /> Rank tag
        </button>
      </div>

      <div className={styles.essField}>
        <label className={styles.essLabel}>Nom (référencé par les grades)</label>
        <input className={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. supreme" />
        {slug && <span className={styles.muted}>Fichier : textures/entity/{folder}/{fileBase}.png</span>}
      </div>

      <div className={styles.essField}>
        <label className={styles.essLabel}>Texture (PNG)</label>
        <label className={styles.secondaryBtn} style={{ width: 'fit-content', cursor: 'pointer' }}>
          <Upload size={14} /> {file ? file.name : 'Choisir un PNG'}
          <input type="file" accept="image/png" style={{ display: 'none' }} onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        </label>
        {preview && (
          <div className={styles.cosmeticPreview} style={{ marginTop: 6 }}>
            <img src={preview} alt="aperçu" />
          </div>
        )}
      </div>

      {feedback && <span className={feedback.ok ? styles.okMsg : styles.error}>{feedback.msg}</span>}
      {log.length > 0 && (
        <div className={styles.essBlock}>
          {log.map((l, i) => <div key={i} className={styles.muted}>{l}</div>)}
        </div>
      )}

      <div className={styles.essActions}>
        <button className={styles.primaryBtn} onClick={create} disabled={creating || !slug || !file}>
          {creating ? <Loader2 size={14} className={styles.spin} /> : <Plus size={14} />} Créer le cosmetic
        </button>
      </div>
      <p className={styles.muted}>
        Le cosmetic est déposé dans le resource pack premium et déclaré dans la config de tous les containers du projet.
        Publie ensuite le pack (Fichiers/Déploiement) et les containers (Serveurs) pour l'appliquer en jeu, puis assigne-le à un grade dans l'onglet Apparence.
      </p>

      <CosmeticList type={type} refreshKey={listRefresh} />
    </div>
  );
}
